import { getStoredSessionToken } from "./appleAuth";
import type { ReadingPackSummaryDto } from "./types";

export const SYNC_PACKS_PER_INVOKE = 10;

export type SyncPreviewResponse = {
  month: string;
  packs: ReadingPackSummaryDto[];
  warnings: string[];
  firestoreConfigured: boolean;
  packsPerInvoke: number;
  configured: boolean;
  environment: string;
  projectId: string | null;
};

export type SyncPackResult = {
  recordName: string;
  dateKey: string;
  languageCode: string;
  firestorePath: string;
  ok: boolean;
  error?: string;
};

export type SyncBatchResult = {
  runId: string;
  direction: "cloudkit_to_firestore";
  month: string | null;
  attempted: number;
  succeeded: number;
  failed: number;
  daysSynced: number;
  results: SyncPackResult[];
  errorSummary: string | null;
};

export type SyncStatusResponse = {
  direction: string;
  packsPerInvoke: number;
  firestoreConfigured: boolean;
  cloudKitConfigured: boolean;
  projectId: string | null;
  recentRuns: Array<{
    runId: string;
    status: string;
    packsSynced: number | null;
    errorSummary: string | null;
    startedAt: string | null;
    finishedAt: string | null;
  }>;
  notes: string;
};

export type SyncProgress = {
  chunkIndex: number;
  chunkTotal: number;
};

async function authHeader(): Promise<HeadersInit> {
  const token = getStoredSessionToken();
  if (!token) throw new Error("Sign in required.");
  return { Authorization: `Bearer ${token}` };
}

async function readJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data;
}

export async function fetchSyncStatus(): Promise<SyncStatusResponse> {
  const headers = new Headers(await authHeader());
  const res = await fetch("/api/sync/status", { headers });
  return readJson(res);
}

export async function fetchSyncPreview(month: string): Promise<SyncPreviewResponse> {
  const headers = new Headers(await authHeader());
  const qs = new URLSearchParams({ month });
  const res = await fetch(`/api/sync/preview?${qs}`, { headers });
  return readJson(res);
}

function chunkNames(names: string[], size = SYNC_PACKS_PER_INVOKE): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < names.length; i += size) {
    chunks.push(names.slice(i, i + size));
  }
  return chunks;
}

export async function runFirestoreSync(
  month: string,
  recordNames: string[],
  onProgress?: (progress: SyncProgress) => void,
): Promise<SyncBatchResult> {
  const unique = [...new Set(recordNames.map((n) => n.trim()).filter(Boolean))];
  if (unique.length === 0) throw new Error("Select at least one published pack.");

  const chunks = chunkNames(unique);
  const merged: SyncBatchResult = {
    runId: "",
    direction: "cloudkit_to_firestore",
    month,
    attempted: 0,
    succeeded: 0,
    failed: 0,
    daysSynced: 0,
    results: [],
    errorSummary: null,
  };

  for (let i = 0; i < chunks.length; i++) {
    onProgress?.({ chunkIndex: i + 1, chunkTotal: chunks.length });
    const headers = new Headers(await authHeader());
    headers.set("Content-Type", "application/json");
    const res = await fetch("/api/sync/run", {
      method: "POST",
      headers,
      body: JSON.stringify({ month, recordNames: chunks[i] }),
    });
    const chunk = await readJson<SyncBatchResult>(res);
    merged.runId = chunk.runId || merged.runId;
    merged.attempted += chunk.attempted;
    merged.succeeded += chunk.succeeded;
    merged.failed += chunk.failed;
    merged.daysSynced += chunk.daysSynced;
    merged.results.push(...chunk.results);
  }

  if (merged.failed > 0) {
    merged.errorSummary = merged.results
      .filter((r) => !r.ok)
      .map((r) => `${r.recordName}: ${r.error ?? "failed"}`)
      .slice(0, 5)
      .join(" · ");
  }

  return merged;
}
